import Style from 'ol/style/Style.js';
import Stroke from 'ol/style/Stroke.js';
import Fill from 'ol/style/Fill.js';
import CircleStyle from 'ol/style/Circle.js';
import Text from 'ol/style/Text.js';

/**
 * Creates an OpenLayers style array based on a config object.
 * Implements "Halo" effect for better contrast.
 * @param {Object} config 
 * @param {string} [textValue] - The text content to display
 * @returns {Style[]}
 */
export const getStyleFromConfig = (config, textValue) => {
    const { strokeColor, strokeWidth, fillColor, fillOpacity, pointRadius, lineStyle } = config;
    
    // Parse stroke color to ensure we can use it for the point fill if needed
    // Assuming strokeColor is hex for now as per DrawTool.vue state
    
    // Generate RGBA for fill
    const fillRGBA = hexToRGBA(rgbaToHex(fillColor) || fillColor, fillOpacity);

    // Line Dash Mapping
    let lineDash = null;
    if (lineStyle === 'Segmentada') lineDash = [10, 10];
    if (lineStyle === 'Punteada') lineDash = [2, 5];

    const content = textValue || '';
    const hasText = content.length > 0;
    const fontSize = 14 + (Number(strokeWidth) - 1) * 3;
    const font = `bold ${fontSize}px sans-serif`;

    // Style 0: The Halo (White background stroke)
    const haloStyle = new Style({
        stroke: new Stroke({
            color: '#ffffff',
            width: Number(strokeWidth) + 2,
            lineDash: lineDash
        }),
        image: hasText ? null : new CircleStyle({
            radius: Number(pointRadius) + 1,
            stroke: new Stroke({
                color: '#ffffff',
                width: 3,
            }),
        }),
        text: new Text({
            text: content,
            font: font,
            textAlign: 'center',
            textBaseline: 'middle',
            offsetX: 0,
            offsetY: 0,
            stroke: new Stroke({
                color: '#ffffff',
                width: 3
            })
        })
    });

    // Style 1: The Main Style
    const mainStyle = new Style({
        stroke: new Stroke({
            color: strokeColor,
            width: Number(strokeWidth),
            lineDash: lineDash
        }),
        fill: new Fill({
            color: fillRGBA,
        }),
        image: hasText ? null : new CircleStyle({
            radius: Number(pointRadius),
            fill: new Fill({
                color: strokeColor,
            }),
            stroke: new Stroke({
                color: '#ffffff',
                width: 1,
            }),
        }),
        text: new Text({
            text: content,
            font: font,
            textAlign: 'center',
            textBaseline: 'middle',
            offsetX: 0,
            offsetY: 0,
            fill: new Fill({
                color: strokeColor
            })
        })
    });

    return [haloStyle, mainStyle];
};

const hexToRGBA = (hex, alpha) => {
    if (!hex || typeof hex !== 'string' || !hex.startsWith('#')) return hex;
    const r = Number.parseInt(hex.slice(1, 3), 16);
    const g = Number.parseInt(hex.slice(3, 5), 16);
    const b = Number.parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const rgbaToHex = (rgba) => {
    if (!rgba || typeof rgba !== 'string' || !rgba.startsWith('rgba')) return rgba;
    const parts = rgba.substring(5, rgba.length - 1).split(',');
    const r = Number.parseInt(parts[0]).toString(16).padStart(2, '0');
    const g = Number.parseInt(parts[1]).toString(16).padStart(2, '0');
    const b = Number.parseInt(parts[2]).toString(16).padStart(2, '0');
    return `#${r}${g}${b}`;
};

export const drawStyle = {
    data() {
        return {
            activeStyleArray: null,
        }
    },
    methods: {
        createDrawStyle: function() {
            // Style Reuse: Use a single instance for interaction and layer
            if (!this.activeStyleArray) {
                this.activeStyleArray = getStyleFromConfig(this.drawStyleConfig);
            }
            return this.activeStyleArray;
        },
        updateDrawStyle: function (currentDrawingType) {
            const styles = this.createDrawStyle();
            const config = this.drawStyleConfig;
            const isText = currentDrawingType === 'Text';
            
            const halo = styles[0];
            const main = styles[1];

            const fillRGBA = hexToRGBA(rgbaToHex(config.fillColor) || config.fillColor, config.fillOpacity);

            // Line Dash Mapping
            let lineDash = null;
            if (config.lineStyle === 'Segmentada') lineDash = [10, 10];
            if (config.lineStyle === 'Punteada') lineDash = [2, 5];

            const fontSize = 14 + (Number(config.strokeWidth) - 1) * 3;
            const font = `bold ${fontSize}px sans-serif`;

            // Update Halo
            halo.getStroke().setWidth(Number(config.strokeWidth) + 2);
            halo.getStroke().setLineDash(lineDash);
            
            // Halo Image (only if not text)
            if (isText) {
                halo.setImage(null);
            } else {
                let haloImage = halo.getImage();
                if (!haloImage) {
                    haloImage = new CircleStyle({
                        radius: Number(config.pointRadius) + 1,
                        stroke: new Stroke({
                            color: '#ffffff',
                            width: 3,
                        }),
                    });
                    halo.setImage(haloImage);
                } else {
                    haloImage.setRadius(Number(config.pointRadius) + 1);
                }
            }
            
            if (halo.getText()) {
                halo.getText().setText(''); // Interaction sketch usually has no text
                halo.getText().setFont(font);
            }

            // Update Main
            main.getStroke().setColor(config.strokeColor);
            main.getStroke().setWidth(Number(config.strokeWidth));
            main.getStroke().setLineDash(lineDash);
            main.getFill().setColor(fillRGBA);

            // Main Image (only if not text)
            if (isText) {
                main.setImage(null);
            } else {
                let mainImage = main.getImage();
                if (!mainImage) {
                    mainImage = new CircleStyle({
                        radius: Number(config.pointRadius),
                        fill: new Fill({
                            color: config.strokeColor,
                        }),
                        stroke: new Stroke({
                            color: '#ffffff',
                            width: 1,
                        }),
                    });
                    main.setImage(mainImage);
                } else {
                    mainImage.setRadius(Number(config.pointRadius));
                    mainImage.getFill().setColor(config.strokeColor);
                }
            }

            if (main.getText()) {
                main.getText().setText(''); // Interaction sketch usually has no text
                main.getText().getFill().setColor(config.strokeColor);
                main.getText().setFont(font);
            }

            // Trigger redraw on the layer. 
            if (this.drawLayer) {
                this.drawLayer.changed();
            }
        },
        // Helper to expose to template
        rgbaToHex(rgba) {
            return rgbaToHex(rgba);
        },
        onFillColorChange(event) {
            const hex = event.target.value;
            this.drawStyleConfig.fillColor = hex;
            this.updateDrawStyle(this.currentDrawingType);
        },
    }
}
